import { EventEmitter, Component, Output, Input, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { AbstractControl, ValidationErrors, Validators, FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { IStockComm, IStockFolder, IxellAPIService } from 'src/app/tools/api.service';
import { FileCreateCommModalResult, GroupedLocale } from '../cloud/cloud.component';
import { checkForExtension, downloadBase64File } from '../utils/utils';
import moment, { Moment } from 'moment';
import { MatSelectChange } from '@angular/material/select';


@Component({
    selector: 'cloud-create-comm-modal',
    templateUrl: './create-comm-modal.component.html',
    styleUrls: ['./create-comm-modal.component.scss']
})

export class CloudCreateCommModalComponent {
    COMM_MAX_FILE_SIZE = ((1 << 20) * 4);

    @Input() folder: IStockFolder;
    @Input() file: IStockComm;
    @Input() possibleLocales: { [key: number]: GroupedLocale };
    @Input() uploadProgress: number;
    @Input() initialCountry: number;
    @Input() tarifCategories: { [idPays: number]: string[] };
    @Input() availableTechnologies: { name: string, code: string }[];
    @Output() close = new EventEmitter<void>();
    @Output() save = new EventEmitter<FileCreateCommModalResult>();
    @Output() reset = new EventEmitter<void>();

    @ViewChildren('fileInputList') fileInputList!: QueryList<ElementRef<HTMLInputElement>>[];

    commKind: number = 0;
    selectedCountry = 1;
    selectedTariff = '';
    selectedTechnology = '';

    isDownloading = false;

    readonly datenow: Date = new Date();
    readonly acceptedExtensions = ['pdf', 'png'];
    private readonly fileValidator = (idx: number) => async (control: AbstractControl): Promise<ValidationErrors | null> => {
        if (!this.selectedFiles[idx])
            return null;

        if (this.selectedFiles[idx].size > this.COMM_MAX_FILE_SIZE) // 4 Mo
            return { fileTooBig: true };

        if (!await checkForExtension(this.selectedFiles[idx], this.acceptedExtensions[idx]))
            return { invalidExtension: true };

        return null;
    };

    readonly fileKind: number[] = [0, 0];
    readonly selectedFiles: File[] = [null, null];
    readonly selectedFilenames = [new FormControl<string>('', [Validators.required], [this.fileValidator(0)]), new FormControl<string>('', [Validators.required], [this.fileValidator(1)])];
    readonly chosenTitle = new FormControl<string>('', [Validators.required]);
    readonly startDate = new FormControl<Moment>(moment(new Date()), [Validators.required]);
    readonly endDate = new FormControl<Moment>(
        (() => {
            const date = new Date();
            date.setDate(date.getDate() + 7);
            return moment(date);
        })(),
        [Validators.required, (control: AbstractControl): ValidationErrors | null => {
            if (this.startDate.value > control.value)
                return { mustBeSuperior: true };

            return null;
        }]
    );

    formsDisabled: boolean = false;
    formsGlobalError: string = '';

    private langueSub: any;
    private getCloudCommFileSub: any;

    constructor(private readonly apiService: IxellAPIService, private readonly translateService: TranslateService) { }

    ngOnInit() {
        if (!this.folder)
            this.changeFormState(true);

        if (this.file) {
            this.commKind = (this.file.bannerFilename) ? 0 : 1;
            this.chosenTitle.setValue(this.file.title);
            this.startDate.setValue(moment(this.file.startingDate));
            this.endDate.setValue(moment(this.file.endingDate));
            this.selectedTariff = this.file.tariff ?? '';
            this.selectedTechnology = this.file.tech ?? '';
            this.selectedCountry = this.file.paysId;
            this.onCountryChanged({ source: null, value: this.selectedCountry });
            this.fileKind[0] = (this.file.docFilename) ? 1 : 0;
            this.fileKind[1] = (this.file.bannerFilename) ? 1 : 0;
        }
        else {
            this.selectedCountry = this.initialCountry;
            this.fileKind[0] = 0;
            this.fileKind[1] = 0;
        }
    }

    ngOnDestroy() {
        if (this.langueSub != null) this.langueSub.unsubscribe();
        if (this.getCloudCommFileSub != null) this.getCloudCommFileSub.unsubscribe();
    }

    onFileSelected(fileInput: HTMLInputElement, i: number) {
        this.selectedFiles[i] = (fileInput.files.length > 0) ? fileInput.files[0] : null;
        this.selectedFilenames[i].setValue((fileInput.files.length > 0) ? fileInput.files[0].name : null);
        this.selectedFilenames[i].markAsTouched();
    }

    onFileReset(fileInput: HTMLInputElement) {
        fileInput.value = null;
        fileInput.dispatchEvent(new Event('change'));
    }

    onCountryChanged(event: MatSelectChange) {
        if (!this.tarifCategories[event.value].includes(this.selectedTariff))
            this.selectedTariff = '';
    }

    getLanguage(locale: GroupedLocale, langId: number) {
        return locale.langues.find(l => l.idLangue == langId);
    }

    downloadFile(isBanner: boolean) {
        if (!this.isDownloading)
            this.isDownloading = true;
        else
            return;

        console.log(this.file)

        if (isBanner && this.file.bannerFile) {
            downloadBase64File(this.file.bannerFile, this.file.bannerFilename);
            this.isDownloading = false;
        }
        else if (!isBanner && this.file.docFile) {
            downloadBase64File(this.file.docFile, this.file.docFilename);
            this.isDownloading = false;
        }
        else {
            this.getCloudCommFileSub = this.apiService.getCloudCommFile(this.file.idStockComm, isBanner).subscribe(data => {
                if (isBanner) {
                    this.file.bannerFile = data;
                    downloadBase64File(this.file.bannerFile, this.file.bannerFilename);
                }
                else {
                    this.file.docFile = data;
                    downloadBase64File(this.file.docFile, this.file.docFilename);
                }

                this.isDownloading = false;

                if (this.getCloudCommFileSub != null) this.getCloudCommFileSub.unsubscribe();
            }, error => {
                if (this.getCloudCommFileSub != null) this.getCloudCommFileSub.unsubscribe();
            })
        }
    }

    doClose() {
        this.close.emit();
    }

    doSave() {
        this.formsGlobalError = '';
        this.reset.emit();

        // Check for bad form inputs

        let bad = this.chosenTitle.invalid || this.startDate.invalid || this.endDate.invalid;
        if (!bad) {
            for (let i = 0; i < this.selectedFilenames.length; i++) {
                if (this.fileKind[i] === 1) {
                    this.selectedFilenames[i].markAsUntouched();
                }
                else if ((i !== 1 || this.commKind !== 1) && (this.selectedFilenames[i].invalid || this.selectedFiles[i].size > ((1 << 20) * 200))) {
                    bad = true;
                }
            }
        }

        if (bad) {
            this.chosenTitle.markAsTouched();
            this.startDate.markAsTouched();
            this.endDate.markAsTouched();
            for (let i = 0; i < this.selectedFilenames.length; i++) {
                if (this.fileKind[i] !== 1) {
                    this.selectedFilenames[i].markAsTouched();
                }
            }

            return;
        }

        const startDate = new Date(Date.UTC(this.startDate.value.toDate().getFullYear(), this.startDate.value.toDate().getMonth(), this.startDate.value.toDate().getDate(), 0, 0, 0));
        const endDate = new Date(Date.UTC(this.endDate.value.toDate().getFullYear(), this.endDate.value.toDate().getMonth(), this.endDate.value.toDate().getDate(), 23, 59, 59));

        this.save.emit({
            id: this.file ? this.file.idStockComm : 0,
            folder: this.folder,
            data: this.selectedFiles.map((file, i) => (this.fileKind[i] === 1) ? null : file).slice(0, (this.commKind === 1) ? 1 : 2),
            kind: this.commKind,
            title: this.chosenTitle.value,
            start: startDate,
            end: endDate,
            tariff: this.selectedTariff,
            tech: this.selectedTechnology,
            country: this.selectedCountry,
            formStatusChanger: (state: boolean | string) => this.changeFormState(state)
        });
    }

    changeFormState(state: boolean | string) {
        if (typeof (state) === 'string') {
            this.formsGlobalError = state;
            this.changeFormState(false);
            return;
        }

        this.formsDisabled = state;

        for (const selectedFilename of this.selectedFilenames) {
            if (state)
                selectedFilename.disable();
            else
                selectedFilename.enable();
        }

        if (state) {
            this.chosenTitle.disable();
            this.startDate.disable();
            this.endDate.disable();
        }
        else {
            this.chosenTitle.enable();
            this.startDate.enable();
            this.endDate.enable();
        }
    }
}
